import { BellAlertIcon, BellSlashIcon, PencilSquareIcon, TrashIcon } from '@heroicons/react/24/outline'
import cloneDeep from 'clone-deep'
import { trackReportRun } from 'components/BackgroundRuns'
import { Frequency } from 'components/Frequency'
import { IntevalLabels, RepeatType } from 'components/Frequency/constants'
import { validateFrequencyData } from 'components/Frequency/logic'
import { LayoutLoading } from 'components/LayoutLoading'
import { TagMatch, TagMatchToggle } from 'components/TagMatchToggle'
import { accountTypes, companyName, PipeLineType } from 'config'
import { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { Link, useHistory, useLocation } from 'react-router-dom'
import { toast } from 'react-toastify'
import {
  cancelServicingReportJob,
  downloadMortgageCallXml,
  downloadServicingReportJobFile,
  getAdminToAeEmails,
  getLoanReports,
  getLoanTagSuggestions,
  getMortgageCallXmlPreview,
  getServicingReportJob,
  getServicingReportOptions,
  removeLoanReport,
  saveReports,
  startServicingReportJob,
} from 'services'
import { svgLoading } from 'stories/assets'
import svgSearch from 'stories/assets/search.svg'
import { Button, ButtonGroup, Input, Modal, Tooltip } from 'stories/components'
import { confirm, formatTime, renderHeader, useTitle } from 'utils'
import { RenderInput } from 'utils/RenderInput'

import BalloonNoticesModal from './BalloonNoticesModal'
import BalloonNoticesScheduleModal from './BalloonNoticesScheduleModal'
import BorrowerLateNoticesModal from './BorrowerLateNoticesModal'
import BorrowerLateNoticesScheduleModal from './BorrowerLateNoticesScheduleModal'
import {
  balanceReconciliationReports,
  borrowerReports,
  cashReconciliationReports,
  chargeReports,
  defaultReports,
  investorReports,
  PDF_ONLY_SERVICING_REPORTS,
  regulatoryReports,
  ServicingReportDownloadFormat,
  tmoReports,
  trustLedgerReports,
  XLSX_FIRST_SERVICING_REPORTS,
} from './constant'
import InsuranceExpirationNoticesModal from './InsuranceExpirationNoticesModal'
import InsuranceExpirationNoticesScheduleModal from './InsuranceExpirationNoticesScheduleModal'
import RateChangeNoticesModal from './RateChangeNoticesModal'
import { ILoanReport, LoanReportsOrigin } from './types'

const staticReports: Record<string, string> = {
  hmda: 'HMDA Report',
}

// Standard reports are stored as LoanReports rows tagged with these markers in `type`
// (custom reports use type 'Loan'), so we can attach a schedule to them.
const standardMarkers = new Set<string>([
  ...Object.values(tmoReports),
  ...Object.values(trustLedgerReports),
  ...Object.values(investorReports),
  ...Object.values(regulatoryReports),
  ...Object.values(borrowerReports),
  ...Object.values(cashReconciliationReports),
  ...Object.values(balanceReconciliationReports),
  ...Object.values(chargeReports),
  ...Object.values(defaultReports),
  ...Object.keys(staticReports),
])
const isStandardType = (type?: string) => !!type && standardMarkers.has(type)

// The page's top-level tabs. Origination and servicing reports used to be two separate
// screens (/reports and /reports/servicing); they are now one screen whose origin is a tab.
const originTabs: Record<string, string> = {
  [LoanReportsOrigin.Origination]: 'Origination',
  [LoanReportsOrigin.Servicing]: 'Servicing',
}

export default function Reports({ origin: routeOrigin }: { origin?: LoanReportsOrigin | number }) {
  useTitle(`Reports - ${companyName}`)


  const navigate = useHistory()
  const location = useLocation()
  const pipelineType = useSelector((state: any) => state.pipelineFilters?.pipelineType)
  // Reloads the list when the signed-in person changes, including a user switch that stays on
  // this page. The previous person's reports must not remain after impersonation.
  const viewerKey = useSelector(
    (state: any) =>
      `${state.auth?.profile?.id || ''}|${state.auth?.profile?.email || ''}|${state.auth?.role?.impersonatedBy || ''}`,
  )

  const [data, setData] = useState<ILoanReport[]>([])
  const [loading, setLoading] = useState(false)
  const [filters, setFilters] = useState<Record<string, any>>({
    query: '',
    orderBy: 'title',
    orderDir: 1,
  })
  const [selectedItem, setSelectedItem] = useState<ILoanReport | null>(null)
  const [errorCheck, setErrorCheck] = useState(false)
  const [adminEmails, setAdminEmails] = useState<string[]>([])
  const tabs = ['Standard Reports', 'Custom Reports']
  const [tab, setTab] = useState(tabs[0])
  // Which side the page opens on. `/reports/servicing` still deep-links straight to the Servicing
  // tab, and an explicit `?origin=` (what picking a tab writes) wins over the mode, so a refresh,
  // a bookmark or the back button out of a report comes back to the same side. With neither, a
  // plain /reports opens on the side the user is already working in, which is what the old per-mode
  // nav entry did for them.
  const queryOrigin = useMemo(() => {
    const value = new URLSearchParams(location.search).get('origin')
    if (value === null) return undefined
    return Number(value) === LoanReportsOrigin.Servicing
      ? LoanReportsOrigin.Servicing
      : LoanReportsOrigin.Origination
  }, [location.search])
  const pinnedOrigin = (routeOrigin as LoanReportsOrigin | undefined) ?? queryOrigin
  const originForMode = (type: string) =>
    type === PipeLineType.Servicing ? LoanReportsOrigin.Servicing : LoanReportsOrigin.Origination
  const [origin, setOrigin] = useState<LoanReportsOrigin>(pinnedOrigin ?? originForMode(pipelineType))

  // `pipelineType` can reach the store after this mount, as it does on a reload straight onto
  // /reports, so the mode is followed in an effect rather than read once as an initial value, or a
  // servicer lands on Origination and their own custom reports look like they have gone missing.
  useEffect(() => {
    setOrigin(pinnedOrigin ?? originForMode(pipelineType))
  }, [pinnedOrigin, pipelineType])

  // Picking a tab goes to the canonical /reports?origin=, never /reports/servicing?origin=, so the
  // route's pinned origin cannot fight the choice.
  const onChangeOrigin = (next: LoanReportsOrigin) => {
    setOrigin(next)
    navigate.replace(`/reports?origin=${next}`)
  }

  // Servicing report Options modal
  const todayStr = new Date().toISOString().slice(0, 10)
  const [optionsReport, setOptionsReport] = useState<string | null>(null)
  // The Mortgage Call Report also produces an NMLS upload file, and a preview of what that file
  // would carry — which states, which lines were left for the preparer — so nothing is filed blind.
  const [downloadingXml, setDownloadingXml] = useState(false)
  const [mcrPreview, setMcrPreview] = useState<any>(null)
  const [optAsOf, setOptAsOf] = useState(todayStr)
  const [optTags, setOptTags] = useState<string[]>([])
  const [optTagMatch, setOptTagMatch] = useState<TagMatch>('any')
  const [optExcludeTags, setOptExcludeTags] = useState<string[]>([])
  const [optSchema, setOptSchema] = useState<Array<Record<string, any>>>([])
  const [optExtra, setOptExtra] = useState<Record<string, any>>({})
  // Per-option search text for long multiselects (the investor picker runs to ~100 entries).
  const [optFilter, setOptFilter] = useState<Record<string, string>>({})
  const [allTags, setAllTags] = useState<string[]>([])
  const [optFormat, setOptFormat] = useState<ServicingReportDownloadFormat>('pdf')
  // The run the dialog is currently waiting on. Every run goes through the job endpoints, even the
  // one the user waits for, because that is what makes it cancellable.
  const [reportRun, setReportRun] = useState<{ jobId: string; type: string; fileName: string } | null>(null)
  const [runError, setRunError] = useState('')
  // -1 once the run is actually working; >= 0 is how many runs are ahead of it.
  const [runQueue, setRunQueue] = useState(-1)
  const [startingRun, setStartingRun] = useState(false)
  const [showLateNotices, setShowLateNotices] = useState(false)
  const [showLateNoticeSchedule, setShowLateNoticeSchedule] = useState(false)
  const [showInsuranceNotices, setShowInsuranceNotices] = useState(false)
  const [showInsuranceNoticeSchedule, setShowInsuranceNoticeSchedule] = useState(false)
  const [showBalloonNotices, setShowBalloonNotices] = useState(false)
  const [showBalloonNoticeSchedule, setShowBalloonNoticeSchedule] = useState(false)
  const [showRateNotices, setShowRateNotices] = useState(false)

  // A "your report is ready" notification links here with the job id on it. Pull the file down and
  // clear the parameter so a reload does not download it again.
  const search = location.search
  useEffect(() => {
    const jobId = new URLSearchParams(search).get('job')
    if (!jobId) return
    navigate.replace({ search: '' })
    getServicingReportJob(jobId)
      .then(async (res: any) => {
        const job = res?.job
        if (job?.status === 'done') await downloadServicingReportJobFile(job.jobId, job.fileName)
        else toast(res?.message || 'That report is no longer available to download.', { type: 'warning' })
      })
      .catch(() => toast('That report is no longer available to download.', { type: 'warning' }))
  }, [search])

  // Tag universe for the schedule's Report Options (servicing reports + any custom report).
  useEffect(() => {
    getLoanTagSuggestions('')
      .then((t) => setAllTags(Array.isArray(t) ? t : []))
      .catch(() => {})
  }, [])

  const openReportOptions = (marker: string) => {
    setOptionsReport(marker)
    setRunError('')
    setOptAsOf(todayStr)
    setOptTags([])
    setOptExcludeTags([])
    setOptSchema([])
    setOptExtra({})
    setOptFilter({})
    setOptFormat(XLSX_FIRST_SERVICING_REPORTS.has(marker) ? 'xlsx' : 'pdf')
    setMcrPreview(null)
    getServicingReportOptions(marker)
      .then((res) => {
        const schema = res?.options || []
        setOptSchema(schema)
        // Seed default values.
        const defaults: Record<string, any> = {}
        schema.forEach((o) => {
          if (o.default !== undefined) defaults[o.key] = o.default
        })
        setOptExtra(defaults)
      })
      .catch(() => setOptSchema([]))
  }

  // Everything the Options dialog needs to send for a run.
  const reportRequest = () => ({
    type: optionsReport as string,
    asOf: optAsOf,
    filters: { tags: optTags, tagMatch: optTagMatch, excludeTags: optExcludeTags },
    format: isPdfOnlyReport ? 'pdf' : optFormat,
    ...optExtra,
  })

  /** Start a run and hand back its id, or null when the server refused to start it. */
  const beginRun = async () => {
    if (!optionsReport) return null
    setStartingRun(true)
    setRunError('')
    try {
      const res: any = await startServicingReportJob(reportRequest() as any)
      if (!res?.jobId) {
        setRunError(res?.message || 'Could not start this report.')
        return null
      }
      const format = isPdfOnlyReport ? 'pdf' : optFormat
      return { jobId: res.jobId as string, type: optionsReport, fileName: `${optionsReport.toLowerCase()}.${format}` }
    } catch (e: any) {
      setRunError(e?.message || 'Could not start this report.')
      return null
    } finally {
      setStartingRun(false)
    }
  }

  // Wait for it here: the dialog turns into a progress panel that can be cancelled or handed off.
  const onDownloadReport = async () => {
    const run = await beginRun()
    if (run) {
      setRunQueue(0)
      setReportRun(run)
    }
  }

  // Hand it to the background tray and get out of the way. The run keeps going either way; this
  // only decides who is watching it.
  const onRunInBackground = async () => {
    const run = reportRun || (await beginRun())
    if (!run) return
    trackReportRun(run.jobId, run.type, run.fileName)
    setReportRun(null)
    setOptionsReport(null)
    toast('Running in the background. You will be notified when it is ready.', { type: 'info' })
  }

  const onCancelRun = async () => {
    if (!reportRun) return
    const { jobId } = reportRun
    setReportRun(null)
    await cancelServicingReportJob(jobId).catch(() => null)
  }

  // Poll the run the dialog is waiting on, and download it the moment it lands.
  useEffect(() => {
    if (!reportRun) return
    let live = true
    const timer = setInterval(async () => {
      try {
        const res: any = await getServicingReportJob(reportRun.jobId)
        if (!live) return
        if (res?.success === 0) {
          setReportRun(null)
          setRunError(res?.message || 'Lost track of this run.')
          return
        }
        const job = res?.job
        if (!job) return
        // Reports run one at a time on the server, so a run can sit queued before it starts.
        if (job.status === 'queued' || job.status === 'running') {
          setRunQueue(job.status === 'queued' ? job.queuedBehind ?? 0 : -1)
          return
        }
        setReportRun(null)
        if (job.status === 'done') {
          await downloadServicingReportJobFile(job.jobId, job.fileName)
          setOptionsReport(null)
        } else if (job.status === 'error') {
          setRunError(job.error || 'Failed to generate report')
        }
      } catch {
        // Network hiccup: the next tick tries again.
      }
    }, 1500)
    return () => {
      live = false
      clearInterval(timer)
    }
  }, [reportRun])

  // The Mortgage Call Report is a regulatory return, so its dialog offers the upload file as well
  // as the printed copy.
  const isMcrReport = !!optionsReport && Object.values(regulatoryReports).includes(optionsReport)
  const isPdfOnlyReport = !!optionsReport && (PDF_ONLY_SERVICING_REPORTS.has(optionsReport) || isMcrReport)
  const mcrPeriod: string = optExtra.mcrPeriod || ''

  useEffect(() => {
    if (!isMcrReport || !mcrPeriod) {
      setMcrPreview(null)
      return
    }
    let live = true
    getMortgageCallXmlPreview(mcrPeriod)
      .then((res) => live && setMcrPreview(res?.data || null))
      .catch(() => live && setMcrPreview(null))
    return () => {
      live = false
    }
  }, [isMcrReport, mcrPeriod])

  const onDownloadMcrXml = async () => {
    if (!mcrPeriod) return
    setDownloadingXml(true)
    try {
      await downloadMortgageCallXml(mcrPeriod)
    } catch {
      // download errors surface via the browser
    } finally {
      setDownloadingXml(false)
    }
  }

  const assigneeOptions = useMemo(() => {
    const options = { ...accountTypes }
    adminEmails.forEach((email) => (options[email] = email))
    return options
  }, [adminEmails])

  const filteredData = useMemo(() => {
    // The grid shows only custom reports; standard-report schedule rows are shown as
    // bells on the Standard Reports tab.
    const custom = data.filter(
      (item) =>
        !isStandardType(item.type) &&
        (origin === LoanReportsOrigin.Servicing
          ? item.origin === LoanReportsOrigin.Servicing
          : item.origin !== LoanReportsOrigin.Servicing),
    )
    const query = filters.query.trim().toLowerCase()
    const rows = query
      ? custom.filter(
          (item) => item.title?.toLowerCase().includes(query) || item.createdBy?.toLowerCase().includes(query),
        )
      : custom
    const key = filters.orderBy
    const dir = Number(filters.orderDir) === 1 ? 1 : -1
    return [...rows].sort((a, b) => {
      if (key === 'createdAt') {
        const av = new Date(a.createdAt).getTime() || 0
        const bv = new Date(b.createdAt).getTime() || 0
        if (av !== bv) return (av - bv) * dir
      } else if (key === 'title' || key === 'createdBy') {
        const cmp = String(a[key] || '').localeCompare(String(b[key] || ''), undefined, { sensitivity: 'base' })
        if (cmp !== 0) return cmp * dir
      }
      return (a.id - b.id) * dir
    })
  }, [data, filters.query, filters.orderBy, filters.orderDir, origin])

  // The existing schedule row (if any) for a standard report marker.
  const scheduleRow = (marker: string) => data.find((item) => item.type === marker)

  const filterData = async () => {
    setLoading(true)
    const reports = await getLoanReports()
    setData(reports)
    setLoading(false)
  }

  const onChangeFilter = (key: string, value: any) => {
    const newFilters = cloneDeep(filters)
    newFilters[key] = value
    setFilters(newFilters)
  }

  const onSort = (key: string, sortOrder: number) => {
    const newFilters = Object.assign({}, filters)
    newFilters['orderBy'] = key
    newFilters['orderDir'] = sortOrder
    setFilters(newFilters)
  }

  const headers: Record<string, string | JSX.Element> = {
    no: 'No',
    // type: <span>Type</span>,
    title: <span>Title</span>,
    createdBy: <span>Created By</span>,
    createdAt: 'Created At',
    action: 'Actions',
  }

  useEffect(() => {
    filterData()
    getAdminToAeEmails().then((emails: string[]) => setAdminEmails(emails))
  }, [viewerKey])

  const onCreate = () => {
    navigate.push(`/reports/0?origin=${origin ?? LoanReportsOrigin.Origination}`)
  }

  const onDelete = async (item: ILoanReport) => {
    const content = (
      <div className="text-gray-400 mb-4 text-[18px]">
        Are you sure to remove this report?
        <br />
        <span className="text-gray-600">Title: {item.title}</span>
      </div>
    )
    const result = await confirm(content)
    if (!result) return
    setLoading(true)
    removeLoanReport(item.id)
      .then(() => {
        setData(data.filter((row) => row.id !== item.id))
      })
      .finally(() => setLoading(false))
  }

  const onChangeItem = (key: string, value: any) => {
    const temp: any = cloneDeep(selectedItem)
    temp[key] = value
    setSelectedItem(temp)
  }

  const onSave = () => {
    if (!selectedItem) return
    const hasError = validateFrequencyData(selectedItem.frequency, [], true)
    setErrorCheck(hasError)
    if (hasError) return
    setLoading(true)
    const isStandard = isStandardType(selectedItem.type)
    // Standard reports may be a new schedule row (id 0 → insert) — persist their
    // identity (type/title/origin) too, then reload so the row (and its id) appears.
    const payload: Record<string, any> = { frequency: selectedItem.frequency, sendTo: selectedItem.sendTo }
    if (isStandard) {
      payload.type = selectedItem.type
      payload.title = selectedItem.title
      payload.origin = origin
    }
    // Persist report options (as-of date + tag filter) so the scheduled run applies them —
    // for servicing reports and for any custom report (which carries its own filters).
    if (origin === LoanReportsOrigin.Servicing || !isStandardType(selectedItem.type)) {
      payload.filters = selectedItem.filters || {}
    }
    saveReports(selectedItem.id, payload)
      .then(() => {
        setSelectedItem(null)
        if (isStandard) {
          filterData()
        } else {
          const temp = cloneDeep(data)
          temp.map((item) => {
            if (item.id === selectedItem.id) {
              item.frequency = selectedItem.frequency
              item.sendTo = selectedItem.sendTo
            }
          })
          setData(temp)
        }
      })
      .finally(() => setLoading(false))
  }

  return (
    <div className="Reports-container py-6 px-2">
      <div className="relative shadow1 max-w-screen-2xl m-auto bg-white rounded p-3 md:p-7 sm:text-center lg:text-left w-full">
        <LayoutLoading show={loading} />
        <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
          <div className="flex flex-wrap items-center gap-4">
            <h1 className="text-2xl font-bold">Reports</h1>
            <ButtonGroup
              title={originTabs}
              value={String(origin)}
              onChange={(key: string) => onChangeOrigin(Number(key) as LoanReportsOrigin)}
            />
          </div>
          <ButtonGroup title={tabs} value={tab} onChange={setTab} />
        </div>

        {tab === 'Custom Reports' && (
          <div>
            <div className="flex flex-wrap items-center justify-between mb-3">
              <span className="text-base font-semibold text-gray-700">
                Custom Reports{' '}
                {loading ? (
                  <img src={svgLoading} className="inline w-5 h-5 animate-spin" />
                ) : (
                  `(${filteredData.length})`
                )}
              </span>
              <Button onClick={onCreate}>Create Report</Button>
            </div>
            <div className="grid items-center grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mt-4">
          <Input
            type="search"
            title="Search"
            hasIcon
            icon={svgSearch}
            value={filters.query}
            onChange={(value) => onChangeFilter('query', value)}
          />
        </div>
        <div className="mt-2">
          <div className="table-container mt-4 relative overflow-x-auto shadow-md sm:rounded-lg">
            <table className="w-full text-sm text-left text-gray-900 dark:text-gray-400 pl-6">
              <thead className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide bg-slate-50 border-b border-slate-200 dark:bg-gray-700 dark:text-gray-400">
                <tr>
                  {Object.keys(headers).map((key, index) =>
                    renderHeader({
                      title: headers[key],
                      sortable: key === 'title' || key === 'createdBy' || key === 'createdAt',
                      index,
                      key,
                      sortOrder: filters.orderBy == key ? Number(filters.orderDir) : 0,
                      onSort: (key: string, dir: number) => onSort(key, dir),
                    }),
                  )}
                </tr>
              </thead>
              <tbody className="text-sm text-gray-900">
                {filteredData.map((item, index: number) => (
                  <tr key={index} className={`border ${index % 2 ? 'bg-gray-50' : ''}`}>
                    <td className="p-3">{index + 1}</td>
                    {/* <td className="px-2 py-3">
                      <span>{item.type}</span>
                    </td> */}
                    <td className="px-2 py-3 whitespace-nowrap">
                      <span>{item.title}</span>
                    </td>
                    <td className="px-2 py-3">{item.createdBy}</td>
                    <td className="px-2 py-3">{formatTime(item.createdAt)}</td>
                    <td className="px-2 py-3">
                      <div className="flex items-center gap-2">
                        <span
                          className="p-1 text-sm cursor-pointer text-shade-blue hover-shadow1 rounded"
                          onClick={() => navigate.push(`/reports/${item.id}`)}
                        >
                          <PencilSquareIcon className="w-4 h-4" />
                        </span>
                        {item.canEdit !== false && (
                          <Tooltip
                            message={
                              item?.frequency?.repeatType === RepeatType.Repeat && item?.frequency?.intervalUnit
                                ? IntevalLabels[item.frequency.intervalUnit]
                                : ''
                            }
                          >
                            <span
                              className="cursor-pointer hover-shadow1 p-1 rounded"
                              onClick={() => setSelectedItem(item)}
                            >
                              {item?.frequency?.repeatType === RepeatType.Repeat ? (
                                <BellAlertIcon className="text-shade-blue w-4 h-4" />
                              ) : (
                                <BellSlashIcon className="w-4 h-4" />
                              )}
                            </span>
                          </Tooltip>
                        )}
                        {item.canEdit !== false && (
                          <span
                            className="p-1 text-sm cursor-pointer text-red-500 hover-shadow1 rounded"
                            onClick={() => onDelete(item)}
                          >
                            <TrashIcon className="w-4 h-4" />
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
          </div>
        )}

        {tab === 'Standard Reports' && (
          <>
            {origin == LoanReportsOrigin.Origination && (
          <div className="mt-2">
            <p className="font-bold text-sm mb-1">Standard Reports</p>
            <div className="flex gap-4">
              {Object.keys(staticReports).map((key, index) => {
                const sched = scheduleRow(key)
                const scheduled = sched?.frequency?.repeatType === RepeatType.Repeat
                return (
                  <div key={key} className="flex items-center gap-2">
                    <Link to={`reports/${key}`} className="btn-link">
                      {index + 1}. {staticReports[key]}
                    </Link>
                    <Tooltip message={scheduled ? 'Scheduled — edit automation' : 'Schedule automation'}>
                      <span
                        className="cursor-pointer hover-shadow1 p-1 rounded"
                        onClick={() =>
                          setSelectedItem(
                            sched ||
                              ({ id: 0, type: key, title: staticReports[key], origin, frequency: {}, sendTo: [] } as any),
                          )
                        }
                      >
                        {scheduled ? (
                          <BellAlertIcon className="text-shade-blue w-4 h-4" />
                        ) : (
                          <BellSlashIcon className="w-4 h-4" />
                        )}
                      </span>
                    </Tooltip>
                  </div>
                )
              })}
            </div>
          </div>
        )}

        {origin == LoanReportsOrigin.Servicing && (
          <div className="mt-2">
            <p className="font-bold text-sm mb-4">Servicing Reports</p>
            <div className="flex flex-col gap-2 items-start">
              {Object.keys(tmoReports).map((key, index) => {
                const marker = tmoReports[key]
                const sched = scheduleRow(marker)
                const scheduled = sched?.frequency?.repeatType === RepeatType.Repeat
                return (
                  <div key={key} className="flex items-center gap-2">
                    <Tooltip message="Report options (filters & as-of date)">
                      <button type="button" className="btn-link text-left" onClick={() => openReportOptions(marker)}>
                        {index + 1}. {marker}
                      </button>
                    </Tooltip>
                    <Tooltip message={scheduled ? 'Scheduled — edit automation' : 'Schedule automation'}>
                      <span
                        className="cursor-pointer hover-shadow1 p-1 rounded"
                        onClick={() =>
                          setSelectedItem(
                            sched ||
                              ({
                                id: 0,
                                type: marker,
                                title: marker,
                                origin,
                                frequency: {},
                                sendTo: [],
                                filters: {},
                              } as any),
                          )
                        }
                      >
                        {scheduled ? (
                          <BellAlertIcon className="text-shade-blue w-4 h-4" />
                        ) : (
                          <BellSlashIcon className="w-4 h-4" />
                        )}
                      </span>
                    </Tooltip>
                  </div>
                )
              })}
              <p className="font-bold text-sm mt-3 mb-1 border-t border-slate-100 pt-3">Trust Ledger Reports</p>
              {Object.keys(trustLedgerReports).map((key, index) => {
                const marker = trustLedgerReports[key]
                const sched = scheduleRow(marker)
                const scheduled = sched?.frequency?.repeatType === RepeatType.Repeat
                return (
                  <div key={key} className="flex items-center gap-2">
                    <Tooltip message="Report options (filters, as-of date & period)">
                      <button type="button" className="btn-link text-left" onClick={() => openReportOptions(marker)}>
                        {index + 1}. {marker}
                      </button>
                    </Tooltip>
                    <Tooltip message={scheduled ? 'Scheduled — edit automation' : 'Schedule automation'}>
                      <span
                        className="cursor-pointer hover-shadow1 p-1 rounded"
                        onClick={() =>
                          setSelectedItem(
                            sched ||
                              ({
                                id: 0,
                                type: marker,
                                title: marker,
                                origin,
                                frequency: {},
                                sendTo: [],
                                filters: {},
                              } as any),
                          )
                        }
                      >
                        {scheduled ? (
                          <BellAlertIcon className="text-shade-blue w-4 h-4" />
                        ) : (
                          <BellSlashIcon className="w-4 h-4" />
                        )}
                      </span>
                    </Tooltip>
                  </div>
                )
              })}
              <p className="font-bold text-sm mt-3 mb-1 border-t border-slate-100 pt-3">Investor Reports</p>
              {Object.keys(investorReports).map((key, index) => {
                const marker = investorReports[key]
                const sched = scheduleRow(marker)
                const scheduled = sched?.frequency?.repeatType === RepeatType.Repeat
                return (
                  <div key={key} className="flex items-center gap-2">
                    <Tooltip message="Statement options (as-of date, distribution activity window, tags)">
                      <button type="button" className="btn-link text-left" onClick={() => openReportOptions(marker)}>
                        {index + 1}. {marker}
                      </button>
                    </Tooltip>
                    <Tooltip message={scheduled ? 'Scheduled — edit automation' : 'Schedule automation'}>
                      <span
                        className="cursor-pointer hover-shadow1 p-1 rounded"
                        onClick={() =>
                          setSelectedItem(
                            sched ||
                              ({
                                id: 0,
                                type: marker,
                                title: marker,
                                origin,
                                frequency: {},
                                sendTo: [],
                                filters: {},
                              } as any),
                          )
                        }
                      >
                        {scheduled ? (
                          <BellAlertIcon className="text-shade-blue w-4 h-4" />
                        ) : (
                          <BellSlashIcon className="w-4 h-4" />
                        )}
                      </span>
                    </Tooltip>
                  </div>
                )
              })}
              <p className="font-bold text-sm mt-3 mb-1 border-t border-slate-100 pt-3">Borrower Statements</p>
              {Object.keys(borrowerReports).map((key, index) => {
                const marker = borrowerReports[key]
                const sched = scheduleRow(marker)
                const scheduled = sched?.frequency?.repeatType === RepeatType.Repeat
                return (
                  <div key={key} className="flex items-center gap-2">
                    <Tooltip message="Statement options (as-of date, activity window, tags, maximum statements)">
                      <button type="button" className="btn-link text-left" onClick={() => openReportOptions(marker)}>
                        {index + 1}. {marker}
                      </button>
                    </Tooltip>
                    <Tooltip message={scheduled ? 'Scheduled — edit automation' : 'Schedule automation'}>
                      <span
                        className="cursor-pointer hover-shadow1 p-1 rounded"
                        onClick={() =>
                          setSelectedItem(
                            sched ||
                              ({
                                id: 0,
                                type: marker,
                                title: marker,
                                origin,
                                frequency: {},
                                sendTo: [],
                                filters: {},
                              } as any),
                          )
                        }
                      >
                        {scheduled ? (
                          <BellAlertIcon className="text-shade-blue w-4 h-4" />
                        ) : (
                          <BellSlashIcon className="w-4 h-4" />
                        )}
                      </span>
                    </Tooltip>
                  </div>
                )
              })}
              <p className="font-bold text-sm mt-3 mb-1 border-t border-slate-100 pt-3">Cash Reconciliation Reports</p>
              {Object.keys(cashReconciliationReports).map((key, index) => {
                const marker = cashReconciliationReports[key]
                const sched = scheduleRow(marker)
                const scheduled = sched?.frequency?.repeatType === RepeatType.Repeat
                return (
                  <div key={key} className="flex items-center gap-2">
                    <Tooltip message="Report options (period, date basis, grouping, filters)">
                      <button type="button" className="btn-link text-left" onClick={() => openReportOptions(marker)}>
                        {index + 1}. {marker}
                      </button>
                    </Tooltip>
                    <Tooltip message={scheduled ? 'Scheduled — edit automation' : 'Schedule automation'}>
                      <span
                        className="cursor-pointer hover-shadow1 p-1 rounded"
                        onClick={() =>
                          setSelectedItem(
                            sched ||
                              ({
                                id: 0,
                                type: marker,
                                title: marker,
                                origin,
                                frequency: {},
                                sendTo: [],
                                filters: {},
                              } as any),
                          )
                        }
                      >
                        {scheduled ? (
                          <BellAlertIcon className="text-shade-blue w-4 h-4" />
                        ) : (
                          <BellSlashIcon className="w-4 h-4" />
                        )}
                      </span>
                    </Tooltip>
                  </div>
                )
              })}
              <p className="font-bold text-sm mt-3 mb-1 border-t border-slate-100 pt-3">Balance &amp; Exception Reports</p>
              {Object.keys(balanceReconciliationReports).map((key, index) => {
                const marker = balanceReconciliationReports[key]
                const sched = scheduleRow(marker)
                const scheduled = sched?.frequency?.repeatType === RepeatType.Repeat
                return (
                  <div key={key} className="flex items-center gap-2">
                    <Tooltip message="Report options (as-of date, period, exception thresholds)">
                      <button type="button" className="btn-link text-left" onClick={() => openReportOptions(marker)}>
                        {index + 1}. {marker}
                      </button>
                    </Tooltip>
                    <Tooltip message={scheduled ? 'Scheduled — edit automation' : 'Schedule automation'}>
                      <span
                        className="cursor-pointer hover-shadow1 p-1 rounded"
                        onClick={() =>
                          setSelectedItem(
                            sched ||
                              ({
                                id: 0,
                                type: marker,
                                title: marker,
                                origin,
                                frequency: {},
                                sendTo: [],
                                filters: {},
                              } as any),
                          )
                        }
                      >
                        {scheduled ? (
                          <BellAlertIcon className="text-shade-blue w-4 h-4" />
                        ) : (
                          <BellSlashIcon className="w-4 h-4" />
                        )}
                      </span>
                    </Tooltip>
                  </div>
                )
              })}
              <p className="font-bold text-sm mt-3 mb-1 border-t border-slate-100 pt-3">Default &amp; Foreclosure Reports</p>
              {Object.keys(defaultReports).map((key, index) => {
                const marker = defaultReports[key]
                const sched = scheduleRow(marker)
                const scheduled = sched?.frequency?.repeatType === RepeatType.Repeat
                return (
                  <div key={key} className="flex items-center gap-2">
                    <Tooltip message="Report options (which loans count as in default, minimum days past due)">
                      <button type="button" className="btn-link text-left" onClick={() => openReportOptions(marker)}>
                        {index + 1}. {marker}
                      </button>
                    </Tooltip>
                    <Tooltip message={scheduled ? 'Scheduled \u2014 edit automation' : 'Schedule automation'}>
                      <span
                        className="cursor-pointer hover-shadow1 p-1 rounded"
                        onClick={() =>
                          setSelectedItem(
                            sched ||
                              ({
                                id: 0,
                                type: marker,
                                title: marker,
                                origin,
                                frequency: {},
                                sendTo: [],
                                filters: {},
                              } as any),
                          )
                        }
                      >
                        {scheduled ? (
                          <BellAlertIcon className="text-shade-blue w-4 h-4" />
                        ) : (
                          <BellSlashIcon className="w-4 h-4" />
                        )}
                      </span>
                    </Tooltip>
                  </div>
                )
              })}
              <p className="font-bold text-sm mt-3 mb-1 border-t border-slate-100 pt-3">Charge Reports</p>
              {Object.keys(chargeReports).map((key, index) => {
                const marker = chargeReports[key]
                const sched = scheduleRow(marker)
                const scheduled = sched?.frequency?.repeatType === RepeatType.Repeat
                return (
                  <div key={key} className="flex items-center gap-2">
                    <Tooltip message="Report options (period, category filters)">
                      <button type="button" className="btn-link text-left" onClick={() => openReportOptions(marker)}>
                        {index + 1}. {marker}
                      </button>
                    </Tooltip>
                    <Tooltip message={scheduled ? 'Scheduled — edit automation' : 'Schedule automation'}>
                      <span
                        className="cursor-pointer hover-shadow1 p-1 rounded"
                        onClick={() =>
                          setSelectedItem(
                            sched ||
                              ({
                                id: 0,
                                type: marker,
                                title: marker,
                                origin,
                                frequency: {},
                                sendTo: [],
                                filters: {},
                              } as any),
                          )
                        }
                      >
                        {scheduled ? (
                          <BellAlertIcon className="text-shade-blue w-4 h-4" />
                        ) : (
                          <BellSlashIcon className="w-4 h-4" />
                        )}
                      </span>
                    </Tooltip>
                  </div>
                )
              })}
              <p className="font-bold text-sm mt-3 mb-1 border-t border-slate-100 pt-3">Regulatory Filings</p>
              {Object.keys(regulatoryReports).map((key, index) => {
                const marker = regulatoryReports[key]
                const sched = scheduleRow(marker)
                const scheduled = sched?.frequency?.repeatType === RepeatType.Repeat
                return (
                  <div key={key} className="flex items-center gap-2">
                    <Tooltip message="Filing options (reporting quarter)">
                      <button type="button" className="btn-link text-left" onClick={() => openReportOptions(marker)}>
                        {index + 1}. {marker}
                      </button>
                    </Tooltip>
                    <Tooltip message={scheduled ? 'Scheduled — edit automation' : 'Schedule automation'}>
                      <span
                        className="cursor-pointer hover-shadow1 p-1 rounded"
                        onClick={() =>
                          setSelectedItem(
                            sched ||
                              ({
                                id: 0,
                                type: marker,
                                title: marker,
                                origin,
                                frequency: {},
                                sendTo: [],
                                filters: {},
                              } as any),
                          )
                        }
                      >
                        {scheduled ? (
                          <BellAlertIcon className="text-shade-blue w-4 h-4" />
                        ) : (
                          <BellSlashIcon className="w-4 h-4" />
                        )}
                      </span>
                    </Tooltip>
                  </div>
                )
              })}
              <p className="font-bold text-sm mt-3 mb-1 border-t border-slate-100 pt-3">Notices</p>
              <div className="flex items-center gap-2">
                <Tooltip message="Generate escalating late notices (courtesy → foreclosure) for delinquent borrowers">
                  <button type="button" className="btn-link text-left" onClick={() => setShowLateNotices(true)}>
                    {Object.keys(tmoReports).length + 1}. BORROWER LATE NOTICES
                  </button>
                </Tooltip>
                <Tooltip message="Schedule automatic late notices">
                  <span
                    className="cursor-pointer hover-shadow1 p-1 rounded"
                    onClick={() => setShowLateNoticeSchedule(true)}
                  >
                    <BellSlashIcon className="w-4 h-4" />
                  </span>
                </Tooltip>
              </div>
              <div className="flex items-center gap-2">
                <Tooltip message="Generate escalating insurance-expiration notices (renewal → force-placement)">
                  <button type="button" className="btn-link text-left" onClick={() => setShowInsuranceNotices(true)}>
                    {Object.keys(tmoReports).length + 2}. INSURANCE EXPIRATION NOTICES
                  </button>
                </Tooltip>
                <Tooltip message="Schedule automatic insurance notices">
                  <span
                    className="cursor-pointer hover-shadow1 p-1 rounded"
                    onClick={() => setShowInsuranceNoticeSchedule(true)}
                  >
                    <BellSlashIcon className="w-4 h-4" />
                  </span>
                </Tooltip>
              </div>
              <div className="flex items-center gap-2">
                <Tooltip message="Generate escalating balloon-payment notices for loans nearing their maturity/balloon date">
                  <button type="button" className="btn-link text-left" onClick={() => setShowBalloonNotices(true)}>
                    {Object.keys(tmoReports).length + 3}. BALLOON PAYMENT NOTICES
                  </button>
                </Tooltip>
                <Tooltip message="Schedule automatic balloon notices">
                  <span
                    className="cursor-pointer hover-shadow1 p-1 rounded"
                    onClick={() => setShowBalloonNoticeSchedule(true)}
                  >
                    <BellSlashIcon className="w-4 h-4" />
                  </span>
                </Tooltip>
              </div>
              <div className="flex items-center gap-2">
                <Tooltip message="File and email the borrower's rate, payment and escrow change notices for every loan with a scheduled change">
                  <button type="button" className="btn-link text-left" onClick={() => setShowRateNotices(true)}>
                    {Object.keys(tmoReports).length + 4}. RATE &amp; PAYMENT CHANGE NOTICES
                  </button>
                </Tooltip>
              </div>
            </div>
          </div>
        )}
          </>
        )}
      </div>

      {showLateNotices && (
        <BorrowerLateNoticesModal onClose={() => setShowLateNotices(false)} allTags={allTags} />
      )}

      {showLateNoticeSchedule && (
        <BorrowerLateNoticesScheduleModal onClose={() => setShowLateNoticeSchedule(false)} allTags={allTags} />
      )}

      {showInsuranceNotices && (
        <InsuranceExpirationNoticesModal onClose={() => setShowInsuranceNotices(false)} allTags={allTags} />
      )}

      {showInsuranceNoticeSchedule && (
        <InsuranceExpirationNoticesScheduleModal
          onClose={() => setShowInsuranceNoticeSchedule(false)}
          allTags={allTags}
        />
      )}

      {showBalloonNotices && <BalloonNoticesModal onClose={() => setShowBalloonNotices(false)} allTags={allTags} />}

      {showRateNotices && <RateChangeNoticesModal onClose={() => setShowRateNotices(false)} allTags={allTags} />}

      {showBalloonNoticeSchedule && (
        <BalloonNoticesScheduleModal onClose={() => setShowBalloonNoticeSchedule(false)} allTags={allTags} />
      )}
      {selectedItem && (
        <Modal title="Report Automation" isOpen onOk={onSave} onClose={() => setSelectedItem(null)}>
          <div className="w-[500px] min-h-[400px]">
            <RenderInput
              input={{
                inputType: 'multiselect',
                options: assigneeOptions,
                title: 'Send to',
                value: selectedItem.sendTo,
              }}
              Key="sendTo"
              onChange={(_key: string, value: any) => onChangeItem('sendTo', value)}
            />
            {(origin === LoanReportsOrigin.Servicing || !isStandardType(selectedItem.type)) && (
              <div className="mt-6 rounded-md border border-slate-100 bg-slate-50/60 p-3">
                <p className="mb-2 text-[12px] font-semibold text-gray-600">Report Options (applied each run)</p>
                <RenderInput
                  input={{
                    inputType: 'multiselect',
                    title: 'Include Tags',
                    options: Object.fromEntries(allTags.map((t) => [t, t])),
                    value: Array.isArray(selectedItem.filters?.tags) ? selectedItem.filters?.tags : [],
                  }}
                  Key="tags"
                  onChange={(_k: string, v: any) =>
                    onChangeItem('filters', { ...(selectedItem.filters || {}), tags: Array.isArray(v) ? v : [] })
                  }
                />
                <RenderInput
                  input={{
                    inputType: 'multiselect',
                    title: 'Exclude Tags',
                    options: Object.fromEntries(allTags.map((t) => [t, t])),
                    value: Array.isArray(selectedItem.filters?.excludeTags) ? selectedItem.filters?.excludeTags : [],
                  }}
                  Key="excludeTags"
                  onChange={(_k: string, v: any) =>
                    onChangeItem('filters', { ...(selectedItem.filters || {}), excludeTags: Array.isArray(v) ? v : [] })
                  }
                />
                <p className="mb-2 mt-1 text-[11px] text-gray-400">
                  Leave both empty to include all loans; an excluded tag wins over an included one. The as-of date
                  defaults to the run date; set one below to fix it.
                </p>
                <RenderInput
                  input={{ inputType: 'text', type: 'date', title: 'As of Date (optional)', value: selectedItem.filters?.asOf || '' }}
                  Key="asOf"
                  onChange={(_k: string, v: any) => onChangeItem('filters', { ...(selectedItem.filters || {}), asOf: v })}
                />
              </div>
            )}
            <div className="mt-6">
              <Frequency
                data={selectedItem.frequency}
                onChange={(value: any) => onChangeItem('frequency', value)}
                errorCheck={errorCheck}
                repeatTypeToggle={true}
                showTime={true}
              />
            </div>
          </div>
        </Modal>
      )}

      {/* The run the dialog is waiting on. Its own modal rather than a state inside the options
          modal, so the options form stays exactly as it is and the progress panel can carry its
          own buttons, which the shared Modal footer cannot while `loading`. */}
      {reportRun && (
        <Modal title={`Generating: ${reportRun.type}`} titleOkay="" titleCancel="" isOpen hiddenX>
          <div className="w-[440px]">
            <p className="text-sm text-gray-500">
              {runQueue >= 0
                ? runQueue > 0
                  ? `Waiting for ${runQueue} earlier run to finish. Reports run one at a time so a big one cannot slow the rest of the app.`
                  : 'Waiting to start.'
                : 'This can take a few minutes on a large book.'}{' '}
              Leave this open, or send it to the background and carry on working. You will be
              notified when it is ready.
            </p>
            <div className="mt-4 h-2.5 w-full overflow-hidden rounded-full bg-slate-100">
              <div className="h-full w-1/3 animate-pulse rounded-full bg-shade-blue" />
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <Button color="gray" outline onClick={onCancelRun}>
                Cancel Run
              </Button>
              <Button color="blue" onClick={onRunInBackground}>
                Run in Background
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {optionsReport && !reportRun && (
        <Modal
          title={`Report Options — ${optionsReport}`}
          titleOkay="Download"
          isOpen
          loading={startingRun}
          onOk={onDownloadReport}
          onClose={() => setOptionsReport(null)}
        >
          <div className="w-[440px] min-h-[420px]">
            {runError ? (
              <p className="mb-3 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{runError}</p>
            ) : null}
            {!isMcrReport && (
              <div className="mb-4">
                <p className="text-sm font-medium text-gray-700 mb-2">Download Format</p>
                {isPdfOnlyReport ? (
                  <Tooltip message="This report is document-style and PDF only">
                    <div className="inline-flex items-center gap-2 rounded-xl bg-slate-100 p-1 opacity-70">
                      <span className="rounded-lg bg-white px-4 py-1.5 text-sm font-medium text-shade-blue shadow-sm ring-1 ring-black/5">
                        PDF
                      </span>
                      <span className="rounded-lg px-4 py-1.5 text-sm font-medium text-slate-400 cursor-not-allowed">
                        Excel
                      </span>
                    </div>
                  </Tooltip>
                ) : (
                  <ButtonGroup
                    title={['PDF', 'Excel']}
                    value={optFormat === 'xlsx' ? 'Excel' : 'PDF'}
                    onChange={(v) => setOptFormat(v === 'Excel' ? 'xlsx' : 'pdf')}
                  />
                )}
              </div>
            )}
            {/* A regulatory return covers the whole licensed entity for a quarter, so the tag and
                as-of filters every other servicing report takes do not apply to it — the backend
                ignores them, and offering them here would suggest the filing can be narrowed. */}
            {isMcrReport ? (
              <p className="mb-3 text-sm text-gray-500">
                Choose the quarter being filed. This return covers the whole licensed entity — tag and
                as-of filters do not apply.
              </p>
            ) : (
              <>
                <p className="mb-3 text-sm text-gray-500">
                  Filter the loan data for this report and set an as-of date.
                </p>
                <div>
                  <RenderInput
                    input={{
                      inputType: 'multiselect',
                      title: 'Tags',
                      options: Object.fromEntries(allTags.map((t) => [t, t])),
                      value: optTags,
                    }}
                    Key="tags"
                    onChange={(_k: string, v: any) => setOptTags(Array.isArray(v) ? v : [])}
                  />
                  <TagMatchToggle
                    className="mt-2"
                    value={optTagMatch}
                    tagCount={optTags.length}
                    onChange={setOptTagMatch}
                  />
                  <p className="mt-1 text-[11px] text-gray-400">
                    {allTags.length
                      ? `Leave empty to include all loans; select tags to include only loans with ${
                          optTags.length > 1 && optTagMatch === 'all' ? 'all' : 'any'
                        } of them.`
                      : 'No tags exist yet — add tags to loans to filter by them.'}
                  </p>
                </div>
                <div className="mt-4">
                  <RenderInput
                    input={{ inputType: 'text', type: 'date', title: 'As of Date', value: optAsOf }}
                    Key="asOf"
                    onChange={(_k: string, v: any) => setOptAsOf(v)}
                  />
                </div>
              </>
            )}

            {/* Report-specific options (from the backend schema for this report). */}
            {optSchema.map((opt) => {
              const set = (v: any) => setOptExtra((e) => ({ ...e, [opt.key]: v }))
              if (opt.type === 'checkbox') {
                return (
                  <label key={opt.key} className="mt-4 flex items-center gap-2 text-sm text-gray-600">
                    <input type="checkbox" checked={!!optExtra[opt.key]} onChange={(e) => set(e.target.checked)} />
                    {opt.label}
                  </label>
                )
              }
              if (opt.type === 'select') {
                return (
                  <div key={opt.key} className="mt-4">
                    <RenderInput
                      input={{
                        inputType: 'select',
                        title: opt.label,
                        hasDefaultOption: false,
                        options: Object.fromEntries((opt.options || []).map((o: any) => [o.value, o.label])),
                        value: optExtra[opt.key] ?? opt.default ?? '',
                      }}
                      Key={opt.key}
                      onChange={(_k: string, v: any) => set(v)}
                    />
                  </div>
                )
              }
              if (opt.type === 'multiselect') {
                const selected: string[] = Array.isArray(optExtra[opt.key]) ? optExtra[opt.key] : []
                const toggle = (val: string) =>
                  set(selected.includes(val) ? selected.filter((x) => x !== val) : [...selected, val])
                const all: any[] = opt.options || []
                // Long lists (investors) need a search and a scroll box; short ones (payment codes)
                // read better as a plain wrap.
                const searchable = all.length > 12
                const q = (optFilter[opt.key] || '').toLowerCase()
                const shown = q ? all.filter((o: any) => String(o.label).toLowerCase().includes(q)) : all
                return (
                  <div key={opt.key} className="mt-4">
                    <p className="mb-1 text-sm text-gray-600">
                      {opt.label}
                      {selected.length > 0 && <span className="ml-1 text-[11px] text-gray-400">({selected.length} selected)</span>}
                    </p>
                    {searchable && (
                      <input
                        type="text"
                        className="mb-2 w-full rounded border border-gray-300 px-2 py-1 text-sm"
                        placeholder={`Search ${all.length} options…`}
                        value={optFilter[opt.key] || ''}
                        onChange={(e) => setOptFilter((f) => ({ ...f, [opt.key]: e.target.value }))}
                      />
                    )}
                    <div
                      className={`flex flex-wrap gap-x-4 gap-y-1 ${
                        searchable ? 'max-h-48 overflow-y-auto rounded border border-slate-100 p-2' : ''
                      }`}
                    >
                      {shown.map((o: any) => (
                        <label key={o.value} className="flex items-center gap-1.5 text-sm text-gray-600">
                          <input type="checkbox" checked={selected.includes(o.value)} onChange={() => toggle(o.value)} />
                          {o.label}
                        </label>
                      ))}
                      {!shown.length && <span className="text-[12px] text-gray-400">No matches.</span>}
                    </div>
                  </div>
                )
              }
              return (
                <div key={opt.key} className="mt-4">
                  <RenderInput
                    input={{
                      inputType: 'text',
                      type: opt.type === 'number' ? 'number' : opt.type === 'date' ? 'date' : 'text',
                      title: opt.label,
                      placeholder: opt.placeholder || '',
                      value: optExtra[opt.key] ?? '',
                    }}
                    Key={opt.key}
                    onChange={(_k: string, v: any) => set(v)}
                  />
                </div>
              )
            })}

            {/* The NMLS upload file, alongside the printed copy the OK button produces. What the
                file would carry is shown first: a preparer should see the states covered and the
                lines left for them to complete before the filing leaves the building. */}
            {isMcrReport && (
              <div className="mt-5 rounded border border-slate-200 p-3">
                <p className="text-sm font-medium text-gray-700">NMLS Upload File</p>
                <p className="mt-0.5 text-[11px] text-gray-400">
                  Mortgage Call Report Form Version 6 XML, for upload to NMLS. The Download button above
                  produces the printed copy instead.
                </p>

                {mcrPreview && (
                  <div className="mt-2 text-[12px] text-gray-600">
                    <p>
                      {mcrPreview.states?.length
                        ? `${mcrPreview.states.length} state RMLA section${
                            mcrPreview.states.length === 1 ? '' : 's'
                          }: ${mcrPreview.states.join(', ')}`
                        : 'No state licences configured — set them before filing.'}
                    </p>
                    {!!mcrPreview.readiness?.length && (
                      <p className="mt-1 text-amber-700">Still to configure: {mcrPreview.readiness.join(', ')}.</p>
                    )}
                    {!!mcrPreview.errors?.length && (
                      <p className="mt-1 text-rose-700">
                        {mcrPreview.errors.length} value{mcrPreview.errors.length === 1 ? '' : 's'} the schema
                        rejected — do not upload this file.
                      </p>
                    )}
                    {!!mcrPreview.omitted?.length && (
                      <details className="mt-1">
                        <summary className="cursor-pointer text-gray-500">
                          {mcrPreview.omitted.length} line{mcrPreview.omitted.length === 1 ? '' : 's'} left for you
                          to complete in NMLS
                        </summary>
                        <ul className="mt-1 list-disc pl-4 text-gray-500">
                          {mcrPreview.omitted.map((note: string, i: number) => (
                            <li key={i} className="mt-0.5">
                              {note}
                            </li>
                          ))}
                        </ul>
                      </details>
                    )}
                  </div>
                )}

                <Button
                  className="mt-3"
                  color="gray"
                  loading={downloadingXml}
                  disabled={!mcrPeriod}
                  onClick={onDownloadMcrXml}
                >
                  Download XML
                </Button>
              </div>
            )}
          </div>
        </Modal>
      )}
    </div>
  )
}
